import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, BarChartIcon, CheckCircleIcon } from 'lucide-react';
export function CaseStudies() {
  const cases = [{
    client: 'Edenrobe',
    url: 'edenrobe.com',
    title: "3x Traffic Growth for Pakistan's Leading Fashion Brand",
    tag: 'Fashion | SEO',
    description: "Despite being a household name in physical retail, Edenrobe had low organic search visibility online. Limited keywords ranked on Google's first page, technical SEO was underutilized, and high competition from other fashion brands was keeping them buried in search results.",
    metrics: [{
      label: 'Organic Clicks',
      value: '3x'
    }, {
      label: 'Organic Traffic',
      value: '4x'
    }, {
      label: 'Better Rankings',
      value: '60%'
    }, {
      label: 'Keywords Page 1',
      value: '100+'
    }, {
      label: 'Impressions',
      value: '3x'
    }],
    bullets: ['Optimized meta tags, product descriptions, and headers across all product pages.', 'Created SEO-optimized blog content around seasonal collections and fashion trends.', 'Improved site speed, mobile optimization, fixed crawl errors, and implemented schema markup.', 'A/B tested meta descriptions and titles to maximize click-through rates.'],
    imageColor: 'bg-rose-50'
  }, {
    client: 'Furor Jeans',
    url: 'furorjeans.com',
    title: '2x Clicks & Sales Boost Through SEO',
    tag: 'Fashion | SEO',
    description: 'Furor had lower-than-expected organic traffic and struggled to compete with larger fashion brands in search rankings. Click-through rates were low, and high competition for fashion-related keywords was limiting their online sales potential.',
    metrics: [{
      label: 'Organic Clicks',
      value: '2x'
    }, {
      label: 'Impressions',
      value: '2x'
    }, {
      label: 'Better Rankings',
      value: '50%'
    }, {
      label: 'Higher CTR',
      value: '30%'
    }, {
      label: 'Sales Boost',
      value: 'Yes'
    }],
    bullets: ['Optimized meta tags and headers for high-volume fashion keywords.', 'Created engaging content focused on fashion trends and denim care.', 'Improved mobile site speed and resolved crawl issues.', 'Optimized key product categories to align with high-conversion keywords.'],
    imageColor: 'bg-blue-50'
  }, {
    client: 'US Non-Toxic Brand',
    url: 'Confidential',
    title: 'Seamless WooCommerce to Shopify Migration with Subscription Integration',
    tag: 'Platform Migration | Shopify',
    description: 'The brand needed to migrate from WooCommerce to Shopify while maintaining the integrity of active subscriptions managed via the Recharge app. Critical challenges included ensuring 100% data accuracy and handling subscription complexity across two fundamentally different platforms.',
    metrics: [{
      label: 'Data Migrated',
      value: '100%'
    }, {
      label: 'Downtime',
      value: 'Zero'
    }, {
      label: 'Subscriptions',
      value: 'Preserved'
    }, {
      label: 'Performance',
      value: 'Faster'
    }, {
      label: 'UX',
      value: 'Improved'
    }],
    bullets: ['Built a custom Python-based ETL pipeline for precise data migration.', 'Reformatted customer profiles, orders, and product catalogs to match Shopify schema.', 'Rebuilt all recurring billing logic and renewal dates for Recharge compatibility.', 'Conducted manual and automated validation of every migrated data point.'],
    imageColor: 'bg-emerald-50'
  }];
  return <section id="our-work" className="py-24 bg-white" data-id="element-25">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-27">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold font-heading text-navy mb-4" data-id="element-28">
            Our Work
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600" data-id="element-29">
            Real results for real brands. Here's what we've delivered.
          </motion.p>
        </div>

        <div className="space-y-32" data-id="element-30">
          {cases.map((study, index) => <div key={index} className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-20 items-center`} data-id="element-31">
              {/* Visual Side */}
              <motion.div initial={{
            opacity: 0,
            x: index % 2 !== 0 ? 30 : -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-1/2" data-id="element-32">
                <div className={`aspect-[4/3] rounded-2xl ${study.imageColor} border border-gray-100 shadow-lg overflow-hidden relative group`} data-id="element-33">
                  {/* Abstract Browser Mockup */}
                  <div className="absolute inset-0 flex flex-col" data-id="element-34">
                    <div className="h-10 bg-white/80 backdrop-blur border-b border-gray-200 flex items-center px-4 space-x-2" data-id="element-35">
                      <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-36"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-37"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-38"></div>
                      <div className="ml-4 flex-1 h-5 bg-gray-100 rounded-md flex items-center px-3" data-id="element-39">
                        <span className="text-[10px] text-gray-400 font-medium" data-id="element-40">
                          {study.url}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 p-8 flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-41">
                      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-navy via-transparent to-transparent" data-id="element-42"></div>
                      <BarChartIcon className="w-16 h-16 text-navy/20 mb-6" data-id="element-43" />
                      <h3 className="text-2xl font-bold text-navy mb-2" data-id="element-44">
                        {study.client}
                      </h3>
                      <p className="text-sm font-medium text-teal" data-id="element-45">
                        {study.tag}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Content Side */}
              <motion.div initial={{
            opacity: 0,
            x: index % 2 !== 0 ? -30 : 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-1/2" data-id="element-46">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-teal/10 text-teal mb-6" data-id="element-47">
                  {study.tag}
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold font-heading text-navy mb-6 leading-tight" data-id="element-48">
                  {study.title}
                </h3>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-id="element-49">
                  {study.metrics.map((metric, i) => <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-50">
                      <div className="text-2xl font-bold text-success mb-1" data-id="element-51">
                        {metric.value}
                      </div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-52">
                        {metric.label}
                      </div>
                    </div>)}
                </div>

                <p className="text-gray-600 mb-8 text-base leading-relaxed" data-id="element-53">
                  {study.description}
                </p>

                <div className="space-y-3 mb-8" data-id="element-54">
                  {study.bullets.map((bullet, i) => <div key={i} className="flex items-start" data-id="element-55">
                      <CheckCircleIcon className="w-5 h-5 text-teal shrink-0 mt-0.5 mr-3" data-id="element-56" />
                      <p className="text-sm text-gray-700" data-id="element-57">{bullet}</p>
                    </div>)}
                </div>

                <a href="https://calendly.com/fahad-3techno/strategy-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-navy font-semibold hover:text-teal transition-colors group" data-id="element-58">
                  Get similar results for your brand
                  <ArrowUpRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-59" />
                </a>
              </motion.div>
            </div>)}
        </div>
      </div>
    </section>;
}